import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Users, 
  Clock, 
  Award, 
  CheckCircle, 
  Mail, 
  Phone, 
  User,
  BookOpen,
  Target,
  Star,
  Calendar,
  X,
  MapPin,
  CreditCard
} from 'lucide-react';
import { toast } from 'react-toastify';
import clinicInfo from '../config/clinicInfo';
import { createPublicCourseLead } from '../utils/leads';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { useClinicProfile } from '../context/ClinicProfileContext';
import { useTenantSlug } from '../context/TenantSlugContext';
import { buildWhatsAppUrl } from '../utils/contact';

interface LeadForm {
  name: string;
  email: string;
  phone: string;
  profession: string;
  experience: string;
  interests: string[];
  message: string;
}

interface Course {
  id: string;
  clientId: string;
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  format: string;
  level: string;
  price: string;
  image: string;
  highlights: string[];
  category: string;
  maxStudents: number;
  prerequisites: string;
  enrolledStudents: number;
  status: string;
  startDate?: string; 
  endDate?: string; 
  schedule?: string;
  location?: string;
  paymentMethods?: string[];
  installments?: {
    quantity: number;
    value: string;
  }[];
}

const CursosPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [modalCourse, setModalCourse] = useState<Course | null>(null);
  const [pendingFormData, setPendingFormData] = useState<LeadForm | null>(null);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<LeadForm>();
  const { selectedClient } = useAuth();
  const { profile, isTenantContext } = useClinicProfile();
  const { tenantSlug } = useTenantSlug();
  const effectiveSlug = tenantSlug || selectedClient?.clientSlug || null;

  const fallbackCoursesForView: Course[] = [];

  useEffect(() => {
    const fetchCourses = async () => {
      if (!effectiveSlug) {
        setCourses([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const { data, error } = await supabase.rpc("get_public_courses_by_slug", {
          p_slug: effectiveSlug,
        });

        if (error) {
          console.error('Error fetching courses from Supabase:', error);
          setCourses(fallbackCoursesForView);
          return;
        }

        if (data && data.length > 0) {
          const coursesData = (data as any[]).map((row) => ({
            id: row.courseId,
            clientId: row.clientId,
            title: row.courseTitle,
            subtitle: row.courseSubtitle,
            description: row.courseDescription,
            duration: row.courseDuration,
            format: row.courseFormat,
            level: row.courseLevel,
            price: row.coursePrice,
            image: row.courseImage,
            highlights: row.courseHighlights ?? [],
            category: row.courseCategory,
            maxStudents: row.courseMaxStudents ?? 0,
            prerequisites: row.coursePrerequisites ?? '',
            enrolledStudents: 0,
            status: 'active',
            startDate: row.courseStartDate,
            endDate: row.courseEndDate,
            schedule: row.courseSchedule ?? '',
            location: row.courseLocation ?? '',
            paymentMethods: row.coursePaymentMethods ?? [],
            installments: row.courseInstallments ?? [],
          })) as Course[];
          setCourses(coursesData);
        } else {
          setCourses(fallbackCoursesForView);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
        // Use fallback courses on error
        setCourses(fallbackCoursesForView);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, [effectiveSlug]);

  const formatPrice = (price: string | undefined | null) => {
    if (!price) return '';
    const trimmed = price.trim();
    if (trimmed.startsWith('R$')) return trimmed;
    // If numeric like "1200" or "1.200" => prefix with R$
    return `R$ ${trimmed}`;
  };

  const professions = [
    'Fisioterapeuta',
    'Estudante de Fisioterapia',
    'Médico Pneumologista',
    'Enfermeiro',
    'Educador Físico',
    'Outro profissional da saúde'
  ];

  const experienceLevels = [
    'Estudante/Recém-formado',
    '1-3 anos de experiência',
    '3-5 anos de experiência',
    '5-10 anos de experiência',
    'Mais de 10 anos de experiência'
  ];

  const courseInterests = [
    'Reabilitação Pulmonar',
    'Fisioterapia em UTI',
    'Avaliação Funcional',
    'Ventilação Mecânica',
    'Exercício Terapêutico',
    'Pesquisa Científica'
  ];

  const handleCourseSelection = (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setModalCourse(course);
      setShowCourseModal(true);
    }
  };

  const handleCourseInterest = () => {
    if (modalCourse) {
      setSelectedCourse(modalCourse.id);
      setShowCourseModal(false);
      toast.success(`Curso selecionado: ${modalCourse.title}`);
      
      // Scroll suavemente para o formulário
      setTimeout(() => {
        const formElement = document.getElementById('lead-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  };

  const formatWhatsAppMessage = (data: LeadForm) => {
    const interests = data.interests?.join(', ') || 'Não especificado';
    const selectedCourseInfo = selectedCourse 
      ? courses.find(c => c.id === selectedCourse)?.title || 'Curso não especificado'
      : 'Interesse geral em cursos';

    return `
🎓 *INTERESSE EM CURSOS - REVALIDATIE*

👤 *Nome:* ${data.name}
📧 *Email:* ${data.email}
📱 *Telefone:* ${data.phone}
💼 *Profissão:* ${data.profession}
⭐ *Experiência:* ${data.experience}

🎯 *Curso de Interesse:* ${selectedCourseInfo}
📚 *Áreas de Interesse:* ${interests}

💬 *Mensagem:*
${data.message || 'Nenhuma mensagem adicional'}

---
Enviado através do site da Revalidatie
    `.trim();
  };

  const contactWhatsappFull = isTenantContext
    ? profile?.contactWhatsappFull ||
      profile?.contactPhoneFull ||
      profile?.contactWhatsapp ||
      profile?.contactPhone ||
      ''
    : clinicInfo.contato.whatsappFull;

  const handleConsentAgree = async () => {
    if (!privacyConsent) {
      toast.error('Você deve concordar com o uso dos seus dados para continuar');
      return;
    }
    
    if (!pendingFormData) return;

    setShowConsentBanner(false);
    setPrivacyConsent(false);
    setIsSubmitting(true);
    
    try {
      // Salvar lead no Supabase
      const selectedCourseData = selectedCourse ? courses.find(c => c.id === selectedCourse) : null;
      
      if (!selectedCourseData?.clientId) {
        toast.error("Não foi possível identificar o cliente associado ao curso.");
        throw new Error("Client ID do curso não encontrado");
      }

      if (!effectiveSlug || !selectedCourse) {
        toast.error("Não foi possível identificar o tenant ou o curso selecionado.");
        throw new Error("Slug do tenant ou curso ausente");
      }

      await createPublicCourseLead({
        tenantSlug: effectiveSlug,
        courseId: selectedCourse,
        name: pendingFormData.name,
        email: pendingFormData.email,
        phone: pendingFormData.phone,
        profession: pendingFormData.profession,
        experience: pendingFormData.experience,
        interests: pendingFormData.interests || [],
        message: pendingFormData.message,
      });

      // Enviar via WhatsApp
      const message = formatWhatsAppMessage(pendingFormData);
      const whatsappUrl = buildWhatsAppUrl(contactWhatsappFull, message);

      if (!whatsappUrl) {
        toast.error('Este canal de contato está indisponível no momento.');
        return;
      }
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
      
      // Reset form and show success message
      reset();
      setSelectedCourse(null);
      setPendingFormData(null);
      toast.success('Solicitação realizada com sucesso! Redirecionando para o WhatsApp...');
      
    } catch (error) {
      console.error('Erro ao salvar lead:', error);
      toast.error('Erro ao salvar dados. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsentDecline = () => {
    setShowConsentBanner(false);
    setPrivacyConsent(false);
    setPendingFormData(null);
    toast.info('Solicitação cancelada');
  };

  const onSubmit = async (data: LeadForm) => {
    // Store form data and show consent banner
    setPendingFormData(data);
    setShowConsentBanner(true);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center bg-primary-100 text-primary-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <GraduationCap className="w-4 h-4 mr-2" />
              Cursos Especializados
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-neutral-900 mb-6">
              Cursos de Fisioterapia
              <span className="block text-primary-600">Respiratória</span>
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto mb-8">
              Desenvolva suas competências com nossos cursos especializados, ministrados por 
              profissionais com Doutorado e experiência internacional na área respiratória.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-600">
              <div className="flex items-center">
                <Award className="w-5 h-5 text-primary-600 mr-2" />
                Certificação Reconhecida
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 text-primary-600 mr-2" />
                Turmas Reduzidas
              </div>
              <div className="flex items-center">
                <BookOpen className="w-5 h-5 text-primary-600 mr-2" />
                Base Científica Atualizada
              </div>
            </div>
          </motion.div>

          {/* Courses Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16"
          >
            {courses.map((course) => (
              <motion.div
                key={course.id}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                <div className="relative">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-primary-600">
                    {formatPrice(course.price)}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs font-medium">
                      {course.level}
                    </span>
                    <span className="bg-secondary-100 text-secondary-800 px-2 py-1 rounded text-xs font-medium">
                      {course.format}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
                    {course.title}
                  </h3>
                  <p className="text-primary-600 font-medium mb-3">{course.subtitle}</p>
                  <p className="text-neutral-600 text-sm mb-4 line-clamp-3">{course.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-neutral-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {course.duration}
                    </div>
                    
                    {course.startDate && (
                      <div className="flex items-center text-sm text-neutral-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(course.startDate)}
                        {course.endDate && ` - ${formatDate(course.endDate)}`}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleCourseSelection(course.id)}
                    className="w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 bg-primary-50 text-primary-600 hover:bg-primary-100 hover:shadow-md"
                  >
                    Tenho Interesse
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Lead Form */}
          <motion.div
            id="lead-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto"
          >
            <div className="text-center mb-8">
              <Target className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h2 className="text-3xl font-display font-bold text-neutral-900 mb-4">
                Manifeste seu Interesse
              </h2>
              <p className="text-lg text-neutral-600">
                Preencha o formulário abaixo e nossa equipe entrará em contato com informações 
                detalhadas sobre os cursos, datas e condições especiais.
              </p>
            </div>

            {/* Selected Course Display */}
            {selectedCourse && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-4 mb-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Star className="w-5 h-5 text-primary-600 mr-2" />
                    <span className="text-sm font-medium text-primary-800">
                      Curso selecionado: {courses.find(c => c.id === selectedCourse)?.title}
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                  >
                    Remover
                  </button>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Nome Completo *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      type="text"
                      className="pl-10 w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      {...register('name', { required: 'Nome é obrigatório' })}
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      type="email"
                      className="pl-10 w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Telefone/WhatsApp *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      type="tel"
                      placeholder="(00) 00000-0000"
                      className="pl-10 w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      {...register('phone', { required: 'Telefone é obrigatório' })}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                {/* Profissão */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Profissão *
                  </label>
                  <select
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    {...register('profession', { required: 'Profissão é obrigatória' })}
                  >
                    <option value="">Selecione sua profissão</option>
                    {professions.map((profession) => (
                      <option key={profession} value={profession}>
                        {profession}
                      </option>
                    ))}
                  </select>
                  {errors.profession && (
                    <p className="mt-1 text-sm text-red-600">{errors.profession.message}</p>
                  )}
                </div>

                {/* Experiência */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Nível de Experiência *
                  </label>
                  <select
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    {...register('experience', { required: 'Nível de experiência é obrigatório' })}
                  >
                    <option value="">Selecione seu nível de experiência</option>
                    {experienceLevels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  {errors.experience && (
                    <p className="mt-1 text-sm text-red-600">{errors.experience.message}</p>
                  )}
                </div>
              </div>

              {/* Áreas de Interesse */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-3">
                  Áreas de Interesse (selecione todas que se aplicam)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {courseInterests.map((interest) => (
                    <label key={interest} className="flex items-center">
                      <input
                        type="checkbox"
                        value={interest}
                        className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        {...register('interests')}
                      />
                      <span className="ml-2 text-sm text-neutral-700">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Mensagem */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Mensagem Adicional
                </label>
                <textarea
                  rows={4}
                  placeholder="Conte-nos mais sobre seus objetivos, expectativas ou dúvidas específicas..."
                  className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  {...register('message')}
                />
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Phone className="w-5 h-5 mr-2" />
                      Enviar via WhatsApp
                    </>
                  )}
                </button>
                <p className="mt-3 text-sm text-neutral-500">
                  Ao enviar, você será redirecionado para o WhatsApp para finalizar o contato
                </p>
              </div>
            </form>
          </motion.div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-16 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-display font-bold mb-4">
              Por que escolher nossos cursos?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="flex flex-col items-center">
                <Award className="w-12 h-12 mb-3 text-primary-200" />
                <h4 className="font-semibold mb-2">Professores Doutores</h4>
                <p className="text-sm text-primary-100">
                  Todos os nossos instrutores possuem Doutorado e experiência internacional
                </p>
              </div>
              <div className="flex flex-col items-center">
                <BookOpen className="w-12 h-12 mb-3 text-primary-200" />
                <h4 className="font-semibold mb-2">Base Científica</h4>
                <p className="text-sm text-primary-100">
                  Conteúdo baseado nas evidências científicas mais atuais da área
                </p>
              </div>
              <div className="flex flex-col items-center">
                <Users className="w-12 h-12 mb-3 text-primary-200" />
                <h4 className="font-semibold mb-2">Turmas Reduzidas</h4>
                <p className="text-sm text-primary-100">
                  Máximo de 20 alunos por turma para garantir qualidade no aprendizado
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Course Detail Modal */}
      {showCourseModal && modalCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Modal Header */}
            <div className="relative">
              <img
                src={modalCourse.image}
                alt={modalCourse.title}
                className="w-full h-64 object-cover rounded-t-xl"
              />
              <button
                onClick={() => setShowCourseModal(false)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 text-gray-600 hover:text-gray-800 shadow-lg"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-4 bg-white px-4 py-2 rounded-full">
                <span className="text-lg font-bold text-primary-600">{formatPrice(modalCourse.price)}</span>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium">
                  {modalCourse.level}
                </span>
                <span className="bg-secondary-100 text-secondary-800 px-3 py-1 rounded-full text-sm font-medium">
                  {modalCourse.format}
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {modalCourse.category}
                </span>
              </div>

              <h2 className="text-3xl font-display font-bold text-neutral-900 mb-2">
                {modalCourse.title}
              </h2>
              <p className="text-xl text-primary-600 font-medium mb-6">{modalCourse.subtitle}</p>

              {/* Course Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Informações do Curso</h3>
                    <div className="space-y-3">
                      <div className="flex items-center text-neutral-600">
                        <Clock className="w-5 h-5 mr-3 text-primary-600" />
                        <span><strong>Duração:</strong> {modalCourse.duration}</span>
                      </div>
                      
                      {modalCourse.startDate && (
                        <div className="flex items-center text-neutral-600">
                          <Calendar className="w-5 h-5 mr-3 text-primary-600" />
                          <span>
                            <strong>Data:</strong> {formatDate(modalCourse.startDate)}
                            {modalCourse.endDate && ` até ${formatDate(modalCourse.endDate)}`}
                          </span>
                        </div>
                      )}
                      
                      {modalCourse.schedule && (
                        <div className="flex items-center text-neutral-600">
                          <Clock className="w-5 h-5 mr-3 text-primary-600" />
                          <span><strong>Horário:</strong> {modalCourse.schedule}</span>
                        </div>
                      )}
                      
                      {modalCourse.location && (
                        <div className="flex items-center text-neutral-600">
                          <MapPin className="w-5 h-5 mr-3 text-primary-600" />
                          <span><strong>Local:</strong> {modalCourse.location}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center text-neutral-600">
                        <Users className="w-5 h-5 mr-3 text-primary-600" />
                        <span><strong>Vagas:</strong> {modalCourse.maxStudents} alunos</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  {modalCourse.paymentMethods && modalCourse.paymentMethods.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900 mb-3">Formas de Pagamento</h3>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <CreditCard className="w-5 h-5 mr-3 text-primary-600" />
                          <div className="flex flex-wrap gap-2">
                            {modalCourse.paymentMethods.map((method, index) => (
                              <span key={index} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                                {method}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {modalCourse.installments && modalCourse.installments.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Opções de Parcelamento:</p>
                            <div className="space-y-1">
                              {modalCourse.installments.map((installment, index) => (
                                <div key={index} className="bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                                  <span className="text-green-800 font-medium">
                                    {installment.quantity}x de {installment.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Descrição</h3>
                    <p className="text-neutral-600 leading-relaxed">{modalCourse.description}</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">O que você vai aprender</h3>
                    <div className="space-y-2">
                      {modalCourse.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-neutral-600">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {modalCourse.prerequisites && (
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900 mb-3">Pré-requisitos</h3>
                      <p className="text-neutral-600">{modalCourse.prerequisites}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Button */}
              <div className="text-center pt-6 border-t border-gray-200">
                <button
                  onClick={handleCourseInterest}
                  className="inline-flex items-center px-8 py-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-lg"
                >
                  <Star className="w-5 h-5 mr-2" />
                  Tenho Interesse neste Curso
                </button>
                <p className="mt-3 text-sm text-neutral-500">
                  Clique para preencher o formulário de interesse
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Consent Banner Modal */}
      {showConsentBanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-display font-bold text-neutral-900">
                  Aviso de Privacidade - Consentimento para Uso de Dados
                </h3>
                <button
                  onClick={handleConsentDecline}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="text-neutral-700 space-y-4 mb-6 leading-relaxed">
                <p>
                  Nós, da Revalidatie, levamos a sua privacidade muito a sério. Solicitamos que você forneça alguns dados pessoais (nome, endereço de e-mail e telefone) para que possamos entrar em contato com você e enviar nosso informativo semanal por e-mail, com conteúdos sobre saúde, bem-estar e novidades relacionadas aos nossos serviços.
                </p>
                
                <p className="font-medium">
                  Ao marcar a opção abaixo, você confirma que:
                </p>
                
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Autoriza a utilização dos seus dados pessoais fornecidos para que possamos entrar em contato com você, inclusive via telefone e e-mail.
                  </li>
                </ul>
                
                <p className="text-sm">
                  Seus dados serão tratados com segurança, confidencialidade e em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018). Você poderá retirar seu consentimento e solicitar a exclusão dos seus dados da nossa base a qualquer momento através dos canais de contato disponíveis em nosso site.
                </p>
              </div>
              
              <div className="border-t border-gray-200 pt-4 mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacyConsent}
                    onChange={(e) => setPrivacyConsent(e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    Li, estou ciente e concordo com o uso dos meus dados pessoais conforme descrito acima.
                  </span>
                </label>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleConsentDecline}
                  className="flex-1 px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConsentAgree}
                  disabled={isSubmitting || !privacyConsent}
                  className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processando...
                    </div>
                  ) : (
                    'Confirmar e Enviar'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CursosPage;
